// components/FeatureRenderer/HardwareSelector.jsx
import React, { useState, useEffect } from 'react';
import { fetchCompatibleVendors } from '../../../utils/api-service/frontend';

const HardwareSelector = ({
    feature,
    value = {},
    onChange,
    location,
    values,
    productType
}) => {
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);

    // Get pleat from main values (selected in pleat feature)
    const selectedPleat = values?.pleat;

    // Fetch compatible vendors when hardware type, location, or pleat changes
    useEffect(() => {
        if (location?.country && value.hardwareType && value.hardwareType !== 'NONE') {
            fetchCompatibleVendorsForHardware();
        } else {
            setVendors([]);
            // Clear vendor selection if no hardware type
            if (value.vendor) {
                handleChange('vendor', '');
            }
        }
    }, [location?.country, value.hardwareType, selectedPleat, value.motorizedType]);

    const fetchCompatibleVendorsForHardware = async () => {
        setLoading(true);
        try {
            // Call API to get vendors that have compatible hardware
            const response = await fetchCompatibleVendors({
                country: location?.country,
                hardwareType: value.hardwareType,
                motorizedType: value.motorizedType,
                pleat: selectedPleat,
                productType: productType
            });

            if (response.success) {
                setVendors(response.vendors || []);

                // If current vendor is not in the list, clear selection
                if (value.vendor && !response.vendors.some(v => v._id === value.vendor)) {
                    handleChange('vendor', '');
                }
            }
        } catch (error) {
            console.error('Error fetching compatible vendors:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, fieldValue) => {
        console.log(`HardwareSelector handleChange - field: ${field}, value:`, fieldValue);
        const newValue = { ...value, [field]: fieldValue };

        // Reset dependent fields
        if (field === 'hardwareType') {
            // When switching to MOTORIZED, set default motorizedType
            if (fieldValue === 'MOTORIZED') {
                newValue.motorizedType = 'HOME_AUTOMATION';
            } else {
                delete newValue.motorizedType;
            }
            delete newValue.vendor;
        }
        if (field === 'motorizedType') {
            // Clear vendors to force re-fetch with new motorizedType
            setVendors([]);
        }
        onChange(newValue)
    };

    // Find the field definitions from the feature
    const hardwareTypeField = feature.uiConfig.fields.find(f => f.slug === 'hardwareType');
    const motorizedTypeField = feature.uiConfig.fields.find(f => f.slug === 'motorizedType');

    // Determine if vendor selection should be shown
    const showVendorSelection = value.hardwareType && value.hardwareType !== 'NONE';

    // Determine if hardware selection is complete
    const isHardwareComplete = () => {
        if (value.hardwareType === 'NONE') return true;
        if (value.hardwareType === 'MANUAL') {
            return !!(value.hardwareType && value.vendor);
        }
        if (value.hardwareType === 'MOTORIZED') {
            return !!(value.hardwareType && value.motorizedType && value.vendor);
        }
        return false;
    };

    return (
        <div className="mb-4 p-3 bg-light rounded">
            <h6 className="fw-bold mb-3">{feature.name}</h6>

            {/* Hardware Type Selection */}
            {hardwareTypeField && (
                <div className="mb-3">
                    <label className="form-label fw-medium">
                        {hardwareTypeField.name} <span className="text-danger">*</span>
                    </label>
                    <div className="d-flex flex-wrap gap-3">
                        {hardwareTypeField.options.map(option => (
                            <div className="form-check" key={option.value}>
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="hardwareType"
                                    id={`hardware-${option.value}`}
                                    value={option.value}
                                    checked={value.hardwareType === option.value}
                                    onChange={(e) => handleChange('hardwareType', e.target.value)}
                                />
                                <label className="form-check-label" htmlFor={`hardware-${option.value}`}>
                                    {option.label}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Motorized Type (conditional) */}
            {motorizedTypeField && value.hardwareType === 'MOTORIZED' && (
                <div className="mb-3">
                    <label className="form-label fw-medium">{motorizedTypeField.name}</label>
                    <select
                        className="form-select"
                        value={value.motorizedType || 'HOME_AUTOMATION'}
                        onChange={(e) => handleChange('motorizedType', e.target.value)}
                    >
                        {motorizedTypeField.options.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Vendor Selection - Only shown if hardware type is selected and not NONE */}
            {showVendorSelection && (
                <div className="mb-3">
                    <label className="form-label fw-medium">
                        Vendor <span className="text-danger">*</span>
                    </label>

                    {loading && (
                        <div className="text-center py-2">
                            <div className="spinner-border spinner-border-sm text-primary" role="status">
                                <span className="visually-hidden">Loading vendors...</span>
                            </div>
                            <span className="ms-2 text-muted">Loading compatible vendors...</span>
                        </div>
                    )}

                    {!loading && vendors.length === 0 && location?.country && value.hardwareType && (
                        <div className="text-warning small mb-2">
                            No vendors found providing {value.hardwareType.toLowerCase()} hardware
                            {selectedPleat ? ` compatible with ${selectedPleat} pleat` : ''} in {location.country}
                        </div>
                    )}

                    {!loading && vendors.length > 0 && (
                        <div className="d-flex flex-column gap-2 mt-2">
                            {vendors.map(vendor => (
                                <div className="form-check" key={vendor._id}>
                                    <input
                                        className="form-check-input"
                                        type="radio"
                                        name="vendor"
                                        id={`vendor-${vendor._id}`}
                                        value={vendor._id}
                                        checked={value.vendor === vendor._id}
                                        onChange={(e) => handleChange('vendor', e.target.value)}
                                    />
                                    <label className="form-check-label" htmlFor={`vendor-${vendor._id}`}>
                                        <strong>{vendor.name}</strong>
                                        {vendor.compatibleHardware && (
                                            <small className="text-muted ms-2">
                                                ({vendor.compatibleHardware.tracks} tracks, {vendor.compatibleHardware.motors} motors)
                                            </small>
                                        )}
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Hardware Type NONE message */}
            {value.hardwareType === 'NONE' && (
                <div className="mt-2 p-2 bg-light rounded">
                    <small className="text-muted">
                        <i className="bi bi-info-circle me-1"></i>
                        No hardware selected
                    </small>
                </div>
            )}

            {/* Selection Complete Indicator */}
            {isHardwareComplete() && value.hardwareType !== 'NONE' && (
                <div className="mt-2 p-2 bg-success bg-opacity-10 rounded">
                    <small className="text-success">
                        <i className="bi bi-check-circle me-1"></i>
                        Hardware selection complete
                    </small>
                </div>
            )}
        </div>
    );
};

export default HardwareSelector;